/**
 * Default glyph set and state management for the Glyph Editor.
 * Generates starter glyphs for the full ASCII printable range.
 */

// Unicode categories for filtering
export const CATEGORIES = [
    { value: 'all', label: 'All' },
    { value: 'letter', label: 'Letter' },
    { value: 'number', label: 'Number' },
    { value: 'punctuation', label: 'Punctuation' },
    { value: 'symbol', label: 'Symbol' },
    { value: 'other', label: 'Other' },
];

// Category colors (Runebender-inspired multi-color grid)
export const CATEGORY_COLORS = {
    letter: '#ff6b6b',
    number: '#ffd93d',
    punctuation: '#6bcb77',
    symbol: '#4d96ff',
    other: '#9b59b6',
};

export function getCategory(unicode) {
    if (unicode >= 65 && unicode <= 90) return 'letter';   // A-Z
    if (unicode >= 97 && unicode <= 122) return 'letter';  // a-z
    if (unicode >= 48 && unicode <= 57) return 'number';   // 0-9
    if ([33, 34, 39, 40, 41, 44, 45, 46, 58, 59, 63, 96].includes(unicode)) return 'punctuation';
    if (unicode >= 32 && unicode <= 126) return 'symbol';
    return 'other';
}

export function unicodeLabel(code) {
    return `U+${code.toString(16).toUpperCase().padStart(4, '0')}`;
}

/**
 * Generate default empty glyphs for A-Z, a-z, 0-9, and common punctuation
 */
export function generateDefaultGlyphs() {
    const glyphs = [];
    // Space
    glyphs.push({ name: 'space', unicode: 32, advanceWidth: 250, path: [] });
    // ! through ~  (ASCII 33-126)
    for (let i = 33; i <= 126; i++) {
        glyphs.push({
            name: String.fromCharCode(i),
            unicode: i,
            advanceWidth: 600,
            path: [],
        });
    }
    return glyphs;
}

export const DEFAULT_METADATA = {
    familyName: 'My Font',
    styleName: 'Regular',
    unitsPerEm: 1000,
    ascender: 800,
    descender: -200,
    xHeight: 520,
    capHeight: 720,
    version: '1.0',
    description: '',
    designer: '',
};

/**
 * buildRoundedPath — Non-destructive corner rounding.
 * For any L/M command with cornerRadius > 0, inserts a cubic bezier arc
 * at the corner. Returns a new display-only array of path commands.
 * Supports multi-contour paths (contours separated by Z + M).
 */
export const buildRoundedPath = (path) => {
    if (!path || path.length < 2) return path;

    let hasAnyRadius = false;
    for (const cmd of path) {
        if (cmd.cornerRadius > 0) { hasAnyRadius = true; break; }
    }
    if (!hasAnyRadius) return path;

    // Split into individual contours
    const contours = [];
    let current = [];
    for (const cmd of path) {
        if (cmd.type === 'Z') {
            current.push(cmd);
            contours.push(current);
            current = [];
        } else {
            current.push(cmd);
        }
    }
    if (current.length > 0) contours.push(current);

    const getXY = (cmd) => cmd && cmd.x != null ? { x: cmd.x, y: cmd.y } : null;

    // Process each contour independently
    const finalResult = [];
    for (const contour of contours) {
        const isClosed = contour[contour.length - 1]?.type === 'Z';

        // Collect point indices within this contour
        const pointIndices = [];
        for (let i = 0; i < contour.length; i++) {
            if (contour[i].type !== 'Z' && contour[i].x != null) pointIndices.push(i);
        }

        let contourHasRadius = false;
        for (const cmd of contour) {
            if (cmd.cornerRadius > 0) { contourHasRadius = true; break; }
        }
        if (!contourHasRadius) {
            // No rounding needed for this contour, pass through as-is
            finalResult.push(...contour);
            continue;
        }

        const contourResult = [];
        let closingArc = null;

        for (let pi = 0; pi < pointIndices.length; pi++) {
            const idx = pointIndices[pi];
            const cmd = contour[idx];
            const radius = cmd.cornerRadius || 0;

            if (radius <= 0 || (cmd.type !== 'L' && !(cmd.type === 'M' && isClosed))) {
                contourResult.push({ ...cmd });
                continue;
            }

            let prevIdx, nextIdx;
            if (cmd.type === 'M' && isClosed) {
                prevIdx = pointIndices[pointIndices.length - 1];
                nextIdx = pointIndices.length > 1 ? pointIndices[1] : null;
            } else {
                prevIdx = pi > 0 ? pointIndices[pi - 1] : null;
                nextIdx = pi < pointIndices.length - 1 ? pointIndices[pi + 1] : null;
                if (nextIdx == null && isClosed) nextIdx = pointIndices[0];
            }

            const prev = prevIdx != null ? getXY(contour[prevIdx]) : null;
            const curr = getXY(cmd);
            const next = nextIdx != null ? getXY(contour[nextIdx]) : null;

            const inIsStraight = cmd.type === 'L' || (cmd.type === 'M' && isClosed);
            const nextCmd = nextIdx != null ? contour[nextIdx] : null;
            const outIsStraight = nextCmd && (nextCmd.type === 'L' || nextCmd.type === 'M');

            if (!prev || !next || !inIsStraight || !outIsStraight) {
                contourResult.push({ ...cmd });
                continue;
            }

            const dxIn = prev.x - curr.x;
            const dyIn = prev.y - curr.y;
            const lenIn = Math.hypot(dxIn, dyIn);
            const dxOut = next.x - curr.x;
            const dyOut = next.y - curr.y;
            const lenOut = Math.hypot(dxOut, dyOut);

            if (lenIn < 1 || lenOut < 1) {
                contourResult.push({ ...cmd });
                continue;
            }

            const maxR = Math.min(lenIn, lenOut) * 0.5;
            const r = Math.min(radius, maxR);

            const startX = curr.x + (dxIn / lenIn) * r;
            const startY = curr.y + (dyIn / lenIn) * r;
            const endX = curr.x + (dxOut / lenOut) * r;
            const endY = curr.y + (dyOut / lenOut) * r;

            const k = 0.5523;
            const cp1x = startX + (curr.x - startX) * k;
            const cp1y = startY + (curr.y - startY) * k;
            const cp2x = endX + (curr.x - endX) * k;
            const cp2y = endY + (curr.y - endY) * k;

            if (cmd.type === 'M') {
                contourResult.push({ type: 'M', x: Math.round(endX), y: Math.round(endY), _roundedFrom: idx });
                closingArc = {
                    lineX: Math.round(startX), lineY: Math.round(startY),
                    cp1x: Math.round(cp1x), cp1y: Math.round(cp1y),
                    cp2x: Math.round(cp2x), cp2y: Math.round(cp2y),
                    endX: Math.round(endX), endY: Math.round(endY),
                };
            } else {
                contourResult.push({ type: 'L', x: Math.round(startX), y: Math.round(startY) });
                contourResult.push({
                    type: 'C',
                    x1: Math.round(cp1x), y1: Math.round(cp1y),
                    x2: Math.round(cp2x), y2: Math.round(cp2y),
                    x: Math.round(endX), y: Math.round(endY),
                });
            }
        }

        // Append closing arc and Z for this contour
        if (isClosed) {
            if (closingArc) {
                contourResult.push({ type: 'L', x: closingArc.lineX, y: closingArc.lineY });
                contourResult.push({
                    type: 'C',
                    x1: closingArc.cp1x, y1: closingArc.cp1y,
                    x2: closingArc.cp2x, y2: closingArc.cp2y,
                    x: closingArc.endX, y: closingArc.endY,
                });
            }
            contourResult.push({ type: 'Z' });
        }

        finalResult.push(...contourResult);
    }

    return finalResult;
};

/**
 * normalizeAccentPath — Extract path commands for the given indices
 * and normalize them so the bounding box starts at (0,0).
 * Returns a new path array suitable for storing as an accent.
 */
export const normalizeAccentPath = (path, selectedIndices) => {
    if (!path || !selectedIndices || selectedIndices.length === 0) return [];

    // Collect the selected commands
    const selected = selectedIndices
        .filter(i => i >= 0 && i < path.length)
        .sort((a, b) => a - b)
        .map(i => ({ ...path[i] }));

    if (selected.length === 0) return [];

    // Find bounding box of selected commands
    let minX = Infinity, minY = Infinity;
    for (const cmd of selected) {
        if (cmd.x != null) { minX = Math.min(minX, cmd.x); minY = Math.min(minY, cmd.y); }
        if (cmd.x1 != null) { minX = Math.min(minX, cmd.x1); minY = Math.min(minY, cmd.y1); }
        if (cmd.x2 != null) { minX = Math.min(minX, cmd.x2); minY = Math.min(minY, cmd.y2); }
    }

    if (!isFinite(minX)) return selected;

    // Offset all coordinates to 0,0 origin
    return selected.map(cmd => {
        const out = { ...cmd };
        if (out.x != null) { out.x = Math.round(out.x - minX); out.y = Math.round(out.y - minY); }
        if (out.x1 != null) { out.x1 = Math.round(out.x1 - minX); out.y1 = Math.round(out.y1 - minY); }
        if (out.x2 != null) { out.x2 = Math.round(out.x2 - minX); out.y2 = Math.round(out.y2 - minY); }
        return out;
    });
};

/**
 * insertAccentPath — Merge an accent's path commands into an existing glyph path
 * at the given offset (offsetX, offsetY). Always inserts as a new contour
 * (ensures the accent starts with an M command).
 * Returns the new combined path array and the index of the first inserted command.
 */
export const insertAccentPath = (existingPath, accentPath, offsetX = 0, offsetY = 0) => {
    if (!accentPath || accentPath.length === 0) return { path: existingPath || [], startIdx: -1 };

    const offset = accentPath.map(cmd => {
        const out = { ...cmd };
        if (out.x != null) { out.x = Math.round(out.x + offsetX); out.y = Math.round(out.y + offsetY); }
        if (out.x1 != null) { out.x1 = Math.round(out.x1 + offsetX); out.y1 = Math.round(out.y1 + offsetY); }
        if (out.x2 != null) { out.x2 = Math.round(out.x2 + offsetX); out.y2 = Math.round(out.y2 + offsetY); }
        return out;
    });

    // Ensure accent starts with M
    if (offset[0].type !== 'M') {
        const first = offset[0];
        offset.unshift({ type: 'M', x: first.x || 0, y: first.y || 0 });
    }

    const base = existingPath ? [...existingPath] : [];
    const startIdx = base.length;
    return { path: [...base, ...offset], startIdx };
};
